// @core/UI/gamelements/BgGlow.ts
import { Container, Sprite, Texture } from 'pixi.js';
import { AssetManager } from '@core/Assets';

/**
 * Defines the visual style for the BgGlow component.
 */
export interface BgGlowStyle {
  tint: number;
  alpha: number;
}

/**
 * A generic background glow effect component.
 * It loads a texture and applies tint and alpha styles.
 * This class is designed to be extended by game-specific implementations.
 */
export class BgGlow extends Container {
  protected sprite: Sprite | null = null;
  protected assetManager: AssetManager;

  constructor(assetManager: AssetManager) {
    super();
    this.assetManager = assetManager;
    this.zIndex = -1; // Ensure it renders behind other elements by default
  }

  /**
   * Initializes the component by loading the specified texture.
   * @param assetSlug The game's slug (e.g., 'crash').
   * @param assetKey The path or key for the image asset.
   */
  public async init(assetSlug: string, assetKey: string): Promise<void> {
    try {
      const texture = (await this.assetManager.loadAsset(
        assetSlug,
        assetKey
      )) as Texture;

      this.sprite = new Sprite(texture);
      this.sprite.anchor.set(0.5);
      // Default scaling can be overridden by a subclass if needed
      this.sprite.scale.set(3.5, 2.5);
      this.addChild(this.sprite);
    } catch (error) {
      console.error(
        `[BgGlow] Failed to load background glow texture with key "${assetKey}":`,
        error
      );
    }
  }

  /**
   * Applies a new visual style (tint and alpha) to the sprite.
   * @param style The style object to apply.
   */
  public updateStyle(style: BgGlowStyle): void {
    if (!this.sprite) return;
    this.sprite.tint = style.tint;
    this.sprite.alpha = style.alpha;
  }

  /**
   * Updates the position and layout of the glow effect.
   * @param width The width of the area to center within.
   * @param height The height of the area to center within.
   */
  public updateLayout(width: number, height: number): void {
    if (!this.sprite) return;
    this.sprite.position.set(width / 2, height / 2);
  }

  /**
   * Resets the glow effect to a default style.
   * @param defaultStyle The default style to apply on reset.
   */
  public reset(
    defaultStyle: BgGlowStyle = { tint: 0x000000, alpha: 0.3 }
  ): void {
    if (this.sprite) {
      this.sprite.tint = defaultStyle.tint;
      this.sprite.alpha = defaultStyle.alpha;
    }
  }
}
